import type {
  ReportingGaugeDto,
  ReportingOverviewDto,
  ReportingSeriesPointDto,
  ReportingServiceError,
  ReportingSummaryDto,
  ReportingTotalDto,
} from "./dto";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return undefined;
};

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/** Normalise une réponse qui peut être un tableau, un objet wrappé, ou un singleton. */
export const asUnknownArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (!isRecord(raw)) return [];
  for (const key of ["items", "series", "gauges", "totals", "data"] as const) {
    if (Array.isArray(raw[key])) return raw[key] as unknown[];
  }
  if ("date" in raw || "metricKey" in raw || "name" in raw || "help" in raw) {
    return [raw];
  }
  return [];
};

const GENERATOR_ARTIFACT_KEYS = new Set(["PRINCIPAL", "FEE", "NET"]);

export const parseSeriesPoint = (
  raw: unknown
): ReportingSeriesPointDto | null => {
  if (!isRecord(raw)) return null;
  const date = asString(raw.date);
  const metricKey = asString(raw.metricKey);
  if (!date && !metricKey) return null;
  return {
    date: date ?? "",
    metricKey: metricKey ?? "",
    dimension1: asString(raw.dimension1),
    dimension2: asString(raw.dimension2),
    currency: asString(raw.currency),
    count: asNumber(raw.count),
    sumMinor: asNumber(raw.sumMinor),
  };
};

export const parseSeries = (raw: unknown): ReportingSeriesPointDto[] =>
  asUnknownArray(raw)
    .map(parseSeriesPoint)
    .filter((point): point is ReportingSeriesPointDto => point !== null);

export const parseGauge = (raw: unknown): ReportingGaugeDto | null => {
  if (!isRecord(raw)) return null;
  const name = asString(raw.name);
  const value = asNumber(raw.value);
  if (!name || value === undefined) return null;
  return {
    name,
    help: asString(raw.help),
    value,
    labels: isRecord(raw.labels) ? raw.labels : undefined,
  };
};

export const parseGauges = (raw: unknown): ReportingGaugeDto[] =>
  asUnknownArray(raw)
    .map(parseGauge)
    .filter((gauge): gauge is ReportingGaugeDto => gauge !== null);

const parseTotal = (raw: unknown): ReportingTotalDto | null => {
  if (!isRecord(raw)) return null;
  const metricKey = asString(raw.metricKey);
  if (!metricKey) return null;
  return {
    metricKey,
    currency: asString(raw.currency),
    count: asNumber(raw.count),
    sumMinor: asNumber(raw.sumMinor),
  };
};

export const parseSummary = (raw: unknown): ReportingSummaryDto => {
  if (!isRecord(raw)) return {};
  const totalsSource = Array.isArray(raw.totals) ? raw.totals : [];
  const gaugesSource = raw.gauges;
  return {
    service: asString(raw.service),
    from: asString(raw.from),
    to: asString(raw.to),
    totals: totalsSource
      .map(parseTotal)
      .filter((total): total is ReportingTotalDto => total !== null),
    gauges: parseGauges(gaugesSource),
    generatedAt: asString(raw.generatedAt),
  };
};

const parseErrorEntry = (
  service: string,
  value: unknown
): ReportingServiceError | null => {
  if (typeof value === "string" && value.trim()) {
    return { service, message: value };
  }
  if (isRecord(value)) {
    const message =
      asString(value.message) ??
      asString(value.errorMsg) ??
      asString(value.error);
    if (message) return { service, message };
  }
  return null;
};

export const parseOverviewErrors = (raw: unknown): ReportingServiceError[] => {
  if (!raw) return [];
  if (typeof raw === "string") return [{ service: "gateway", message: raw }];
  if (Array.isArray(raw)) {
    return raw
      .map((entry, index) => {
        if (typeof entry === "string") {
          return { service: String(index), message: entry };
        }
        if (isRecord(entry)) {
          const service =
            asString(entry.service) ??
            asString(entry.name) ??
            String(index);
          return parseErrorEntry(service, entry);
        }
        return null;
      })
      .filter((entry): entry is ReportingServiceError => entry !== null);
  }
  if (!isRecord(raw)) return [];
  return Object.entries(raw)
    .filter(([key]) => !GENERATOR_ARTIFACT_KEYS.has(key))
    .map(([service, value]) => parseErrorEntry(service, value))
    .filter((entry): entry is ReportingServiceError => entry !== null);
};

export const parseOverviewServices = (
  raw: unknown
): Record<string, ReportingSummaryDto> => {
  if (!isRecord(raw)) return {};
  const out: Record<string, ReportingSummaryDto> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (GENERATOR_ARTIFACT_KEYS.has(key)) continue;
    if (!isRecord(value)) continue;
    if (
      Array.isArray(value.totals) ||
      Array.isArray(value.gauges) ||
      typeof value.service === "string"
    ) {
      out[key] = parseSummary(value);
    }
  }
  return out;
};

export const parseOverview = (raw: unknown): ReportingOverviewDto => {
  if (!isRecord(raw)) {
    return { services: {}, errors: [] };
  }
  return {
    from: asString(raw.from),
    to: asString(raw.to),
    services: parseOverviewServices(raw.services),
    errors: parseOverviewErrors(raw.errors),
    generatedAt: asString(raw.generatedAt),
  };
};

export const compactQuery = (
  params: Record<string, string | undefined>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string] => Boolean(entry[1])
    )
  );

export const findTotal = (
  totals: ReportingTotalDto[] | undefined,
  metricKey: string,
  currency?: string
): ReportingTotalDto | undefined =>
  totals?.find(
    (total) =>
      total.metricKey === metricKey &&
      (currency ? total.currency === currency : true)
  );

export const mergeGauges = (
  ...groups: (ReportingGaugeDto[] | undefined)[]
): ReportingGaugeDto[] => {
  const byName = new Map<string, ReportingGaugeDto>();
  for (const group of groups) {
    for (const gauge of group ?? []) {
      byName.set(gauge.name, gauge);
    }
  }
  return [...byName.values()];
};
